// Copyright (c) 2026, IPivot. All rights reserved.
'use strict';

import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Table, Spinner, Badge, Alert } from 'react-bootstrap';
import { StatusContext } from './StatusContext';
import {
    mergeGetSession,
    mergeListEntries,
    mergeSetEntryStatus,
    mergeSetEntryStatusAll,
    mergeDeleteEntry,
    mergeApplyEntry,
    mergeCommit,
    mergeAbort,
    MergeEntry,
    PendingChange,
} from '../data';
import MergeDiffDialog from './MergeDiffDialog';
import DiffHoverPanel from './DiffHoverPanel';
import { Modal } from 'react-bootstrap';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';

function statusBadge(status: string) {
    if (status === 'accepted') return <Badge bg="success">Accepted</Badge>;
    if (status === 'rejected') return <Badge bg="danger">Rejected</Badge>;
    if (status === 'applied') return <Badge bg="success">Applied ✓</Badge>;
    if (status === 'failed') return <Badge bg="danger">Failed</Badge>;
    return <Badge bg="warning" text="dark">Pending</Badge>;
}

function typeBadge(entry: MergeEntry) {
    if (entry.entry_type === 'category') return <Badge bg="info">Cat</Badge>;
    return <Badge bg="secondary">FP</Badge>;
}

function opBadge(op: string) {
    if (op === 'add') return <Badge bg="success">Add</Badge>;
    if (op === 'update') return <Badge bg="warning" text="dark">Change</Badge>;
    if (op === 'delete') return <Badge bg="danger">Delete</Badge>;
    return <Badge bg="secondary">{op}</Badge>;
}

function entryKey(entry: MergeEntry): string {
    const d = entry.data || {};
    return d.fp?.pvkey || d.category?.key || d.before?.pvkey || d.before?.key || '';
}

function entryTitle(entry: MergeEntry): string {
    const d = entry.data || {};
    if (entry.entry_type === 'category') {
        return d.category?.title || d.before?.title || entryKey(entry);
    }
    return d.fp?.title || d.before?.title || entryKey(entry);
}

function matchKey(m: any): string {
    return [m.ip, m.reverse_dns, m.forward_dns, m.recent_dns, m.tls_cert_subj, m.tls_sni, m.tls_alpn]
        .filter(Boolean).join('|') || '(empty)';
}

function entryMatchDelta(entry: MergeEntry): { added: number; removed: number } {
    if (entry.entry_type !== 'fingerprint') return { added: 0, removed: 0 };

    const d = entry.data || {};
    const before = Array.isArray(d.before?.matches) ? d.before.matches : [];
    const after = Array.isArray(d.fp?.matches)
        ? d.fp.matches
        : Array.isArray(d.matches)
            ? d.matches
            : Array.isArray(d.matches?.after)
                ? d.matches.after
                : [];

    if (entry.op === 'add') return { added: after.length, removed: 0 };
    if (entry.op === 'delete') {
        const deleted = before.length > 0 ? before : (Array.isArray(d.matches) ? d.matches : []);
        return { added: 0, removed: deleted.length };
    }

    const beforeKeys = new Set(before.map(matchKey));
    const afterKeys = new Set(after.map(matchKey));
    const added = after.filter((m: any) => !beforeKeys.has(matchKey(m))).length;
    const removed = before.filter((m: any) => !afterKeys.has(matchKey(m))).length;
    return { added, removed };
}

function entryToPendingChange(entry: MergeEntry): PendingChange {
    const d = entry.data || {};
    const isCat = entry.entry_type === 'category';
    const key = entryKey(entry);
    const detail = isCat
        ? { categoryKey: key, before: d.before || null, after: d.category || null }
        : {
            pvkey: key,
            fingerprint: d.fp || null,
            before: d.before || null,
            after: d.fp || null,
            matches: d.matches || d.fp?.matches || null,
          };
    return {
        id: entry.id,
        proposal_key: key,
        proposal_type: entry.op,
        fingerprint_id: null,
        target_fingerprint_id: null,
        title: entryTitle(entry),
        summary: '',
        severity: 'info',
        status: 'todo',
        detail,
        created: entry.created,
        updated: entry.updated,
    };
}

function MergeOpPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [metadata, setMetadata] = useState<any>(null);
    const [entries, setEntries] = useState<MergeEntry[]>([]);
    const [committing, setCommitting] = useState(false);
    const [softDelete, setSoftDelete] = useState(false);
    const [diffChange, setDiffChange] = useState<PendingChange | null>(null);
    const [hoverDiff, setHoverDiff] = useState<PendingChange | null>(null);
    const [rawEntry, setRawEntry] = useState<MergeEntry | null>(null);

    const statusContext = useContext(StatusContext);
    const navigate = useNavigate();

    const counts = {
        created: entries.filter(e => e.status === 'created').length,
        accepted: entries.filter(e => e.status === 'accepted').length,
        rejected: entries.filter(e => e.status === 'rejected').length,
        applied: entries.filter(e => e.status === 'applied').length,
        failed: entries.filter(e => e.status === 'failed').length,
        total: entries.length,
    };

    const stats = {
        addedFingerprints: entries.filter(e => e.entry_type === 'fingerprint' && e.op === 'add').length,
        removedFingerprints: entries.filter(e => e.entry_type === 'fingerprint' && e.op === 'delete').length,
        addedMatches: entries
            .filter(e => e.entry_type === 'fingerprint' && (e.op === 'add' || e.op === 'update'))
            .reduce((sum, e) => {
                const m = e.data?.fp?.matches || e.data?.matches || [];
                if (Array.isArray(m)) return sum + m.length;
                if (Array.isArray(m?.after)) return sum + m.after.length;
                return sum;
            }, 0),
        removedMatches: entries
            .filter(e => e.entry_type === 'fingerprint' && (e.op === 'delete' || e.op === 'update'))
            .reduce((sum, e) => {
                const m = e.data?.before?.matches || (e.op === 'delete' ? (e.data?.matches || []) : []);
                if (Array.isArray(m)) return sum + m.length;
                if (Array.isArray(m?.before)) return sum + m.before.length;
                return sum;
            }, 0),
        addedCategories: entries.filter(e => e.entry_type === 'category' && e.op === 'add').length,
        removedCategories: entries.filter(e => e.entry_type === 'category' && e.op === 'delete').length,
        addedFpCategories: entries
            .filter(e => e.entry_type === 'fingerprint')
            .reduce((sum, e) => {
                if (e.op === 'add') return sum + (e.data?.fp?.categories?.length || 0);
                if (e.op === 'delete') return sum;
                // update: count newly added category keys
                const before = new Set((e.data?.before?.categories || []).map((c: any) => c.key).filter(Boolean));
                const after = (e.data?.fp?.categories || []).map((c: any) => c.key).filter(Boolean);
                return sum + after.filter((k: string) => !before.has(k)).length;
            }, 0),
        removedFpCategories: entries
            .filter(e => e.entry_type === 'fingerprint')
            .reduce((sum, e) => {
                if (e.op === 'delete') return sum + (e.data?.before?.categories?.length || 0);
                if (e.op === 'add') return sum;
                // update: count removed category keys
                const after = new Set((e.data?.fp?.categories || []).map((c: any) => c.key).filter(Boolean));
                const before = (e.data?.before?.categories || []).map((c: any) => c.key).filter(Boolean);
                return sum + before.filter((k: string) => !after.has(k)).length;
            }, 0),
    };

    const refreshList = useCallback(async () => {
        const res = await mergeListEntries();
        setEntries(res.entries || []);
    }, []);

    const loadSession = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await mergeGetSession();
            if (res.session) {
                const s = res.session;
                setMetadata({ name: s.source_name, ...(s.metadata || {}) });
                setEntries(res.entries || []);
            } else {
                setMetadata(null);
                setEntries([]);
            }
        } catch (e: any) {
            setError(e.message || 'Failed to load merge session');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSession();
        window.addEventListener('fbot:mergeSessionChanged', loadSession);
        return () => window.removeEventListener('fbot:mergeSessionChanged', loadSession);
    }, [loadSession]);

    async function setStatus(id: number, status: 'accepted' | 'rejected' | 'created') {
        try {
            await mergeSetEntryStatus(id, status);
            await refreshList();
        } catch (e: any) {
            statusContext.postError(`Failed to set status: ${e.message}`);
        }
    }

    async function setAll(status: 'accepted' | 'rejected' | 'created', force = false) {
        try {
            await mergeSetEntryStatusAll(status, [], force);
            await refreshList();
        } catch (e: any) {
            statusContext.postError(`Failed to set all: ${e.message}`);
        }
    }

    async function handleApplyNow(id: number) {
        try {
            await mergeApplyEntry(id);
            await refreshList();
        } catch (e: any) {
            statusContext.postError(`Apply failed: ${e.message}`);
        }
    }

    async function handleDelete(id: number) {
        try {
            await mergeDeleteEntry(id);
            await refreshList();
        } catch (e: any) {
            statusContext.postError(`Delete failed: ${e.message}`);
        }
    }

    async function handleCommit() {
        setCommitting(true);
        try {
            const res = await mergeCommit(softDelete);
            statusContext.postInfo(`Committed ${res.applied} change(s)${metadata?.name ? ` from ${metadata.name}` : ''}`);
            navigate('/data/merge-db?dir=originals');
        } catch (e: any) {
            statusContext.postError(`Commit failed: ${e.message}`);
        } finally {
            setCommitting(false);
        }
    }

    async function handleCommitAndContinue() {
        setCommitting(true);
        try {
            const res = await mergeCommit(softDelete);
            statusContext.postInfo(`Committed ${res.applied} change(s)${metadata?.name ? ` from ${metadata.name}` : ''}`);
            const primeCommitMsg = String(metadata?.info || '').trim();
            try {
                localStorage.setItem('fbot:lastMergeMetadata', JSON.stringify({
                    info: primeCommitMsg,
                    name: String(metadata?.name || ''),
                    ts: Date.now(),
                }));
            } catch {
                // ignore storage errors
            }
            navigate('/dev/fingerprint-workflow', { state: { primeCommitMsg } });
        } catch (e: any) {
            statusContext.postError(`Commit failed: ${e.message}`);
        } finally {
            setCommitting(false);
        }
    }

    async function handleCancel() {
        try {
            await mergeAbort();
        } catch {
            // ignore
        }
        navigate('/data/merge-db');
    }

    if (loading) {
        return (
            <Container fluid className="px-4 mt-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading merge file...
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="px-4 mt-4">
                <Alert variant="danger">{error}</Alert>
                <Button variant="secondary" onClick={() => navigate('/data/merge-db')}>Back to Queue</Button>
            </Container>
        );
    }

    return (
        <>
        <Container fluid>
            <Row className="px-4">
                <Col>
                    <h3 className="mt-4">{metadata?.name ? `Merge: ${metadata.name}` : 'Merge'}</h3>

                    {metadata && (
                        <div className="mb-3 p-3 rounded" style={{ background: 'rgba(13,202,240,0.07)', border: '1px solid rgba(13,202,240,0.22)' }}>
                            <div className="d-flex gap-4 flex-wrap">
                                {metadata.name && (
                                    <div>
                                        <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>File</div>
                                        <div style={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: 500 }}>{metadata.name}</div>
                                    </div>
                                )}
                                <div>
                                    <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</div>
                                    <div style={{ fontWeight: 500 }}>{metadata.info || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Serial</div>
                                    <div style={{ fontFamily: 'monospace' }}>{metadata.serial || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>FPs</div>
                                    <div style={{ fontFamily: 'monospace' }}>
                                        <span className="text-success">+{stats.addedFingerprints}</span>
                                        {' / '}
                                        <span className="text-danger">−{stats.removedFingerprints}</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Matches</div>
                                    <div style={{ fontFamily: 'monospace' }}>
                                        <span className="text-success">+{stats.addedMatches}</span>
                                        {' / '}
                                        <span className="text-danger">−{stats.removedMatches}</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cat Records</div>
                                    <div style={{ fontFamily: 'monospace' }}>
                                        <span className="text-success">+{stats.addedCategories}</span>
                                        {' / '}
                                        <span className="text-danger">−{stats.removedCategories}</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>FP Cats</div>
                                    <div style={{ fontFamily: 'monospace' }}>
                                        <span className="text-success">+{stats.addedFpCategories}</span>
                                        {' / '}
                                        <span className="text-danger">−{stats.removedFpCategories}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                        <Button variant="primary" title="Commit and return to FP workflow" disabled={committing || counts.accepted === 0} onClick={handleCommitAndContinue}>
                            {committing ? <><Spinner size="sm" animation="border" className="me-1" />Committing...</> : `Commit & Continue (${counts.accepted} accepted)`}
                        </Button>
                        <Button variant="outline-primary" title="Commit all accepted entries" disabled={committing || counts.accepted === 0} onClick={handleCommit}>
                            {committing ? <><Spinner size="sm" animation="border" className="me-1" />Committing...</> : 'Commit'}
                        </Button>
                        <Button variant="outline-secondary" title="Abort merge session and return to queue" disabled={committing} onClick={handleCancel}>Cancel</Button>
                        <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6,
                                        cursor: 'pointer', color: '#999', userSelect: 'none', marginLeft: 8 }}>
                            <input type="checkbox" checked={softDelete}
                                   onChange={e => setSoftDelete(e.target.checked)} disabled={committing} />
                            Soft Delete
                        </label>
                    </div>

                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                        <Button size="sm" variant="success" title="Mark all entries as accepted, including applied and failed" onClick={() => setAll('accepted', true)}>Accept All</Button>
                        <Button size="sm" variant="danger" title="Mark all entries as rejected, including applied and failed" onClick={() => setAll('rejected', true)}>Reject All</Button>
                        <Button size="sm" variant="outline-secondary" title="Reset all non-applied entries to pending" onClick={() => setAll('created')}>Reset All</Button>
                        <Button size="sm" variant="outline-warning" title="Reset all entries to pending, including applied and failed" onClick={() => setAll('created', true)}>Reset All (incl. applied)</Button>
                        <span className="ms-3 text-muted small">
                            {counts.accepted} accepted · {counts.rejected} rejected · {counts.created} pending
                            {counts.applied > 0 && ` · ${counts.applied} applied`}
                            {counts.failed > 0 && ` · ${counts.failed} failed`}
                            {' · '}{counts.total} total
                        </span>
                    </div>

                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Op</th>
                                <th>Key</th>
                                <th>Title</th>
                                <th>Matches</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-muted">No entries</td></tr>
                            )}
                            {entries.map(e => {
                                const terminal = e.status === 'applied' || e.status === 'failed';
                                const pc = entryToPendingChange(e);
                                const matchDelta = entryMatchDelta(e);
                                return (
                                    <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => setDiffChange(pc)}>
                                        <td>{typeBadge(e)}</td>
                                        <td
                                            onMouseEnter={() => setHoverDiff(pc)}
                                            onMouseLeave={() => setHoverDiff(null)}
                                            onClick={ev => { ev.stopPropagation(); setDiffChange(pc); }}
                                            style={{ cursor: 'default' }}
                                        >
                                            {opBadge(e.op)}
                                        </td>
                                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {entryKey(e)}
                                        </td>
                                        <td>{entryTitle(e)}</td>
                                        <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                            <span className="text-success">+{matchDelta.added}</span>
                                            {' / '}
                                            <span className="text-danger">−{matchDelta.removed}</span>
                                        </td>
                                        <td>
                                            {statusBadge(e.status)}
                                            {e.status === 'failed' && e.error && (
                                                <span className="ms-1 text-danger small" title={e.error}>⚠</span>
                                            )}
                                        </td>
                                        <td onClick={ev => ev.stopPropagation()}>
                                            {!terminal && <>
                                                <Button size="sm" variant="outline-success" className="me-1"
                                                    title="Accept this entry"
                                                    disabled={e.status === 'accepted'}
                                                    onClick={() => setStatus(e.id, 'accepted')}>✓</Button>
                                                <Button size="sm" variant="outline-danger" className="me-1"
                                                    title="Reject this entry"
                                                    disabled={e.status === 'rejected'}
                                                    onClick={() => setStatus(e.id, 'rejected')}>✗</Button>
                                            </>}
                                            <Button size="sm" variant="outline-secondary" className="me-1"
                                                title="Reset this entry to pending"
                                                disabled={e.status === 'created'}
                                                onClick={() => setStatus(e.id, 'created')}>↺</Button>
                                            <Button size="sm" variant="outline-info" className="me-1"
                                                title="View raw JSON for this entry"
                                                onClick={() => setRawEntry(e)}>{'{}'}</Button>
                                            {!terminal && <>
                                                <Button size="sm" variant="outline-primary" className="me-1"
                                                    title="Apply this entry immediately"
                                                    onClick={() => handleApplyNow(e.id)}>▶</Button>
                                                <Button size="sm" variant="outline-danger"
                                                    title="Delete this entry from merge table"
                                                    onClick={() => handleDelete(e.id)}>🗑</Button>
                                            </>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>

                </Col>
            </Row>
        </Container>

        <DiffHoverPanel change={hoverDiff} />

        <MergeDiffDialog change={diffChange} onClose={() => setDiffChange(null)} defaultDiffMode="current" />

        <Modal show={Boolean(rawEntry)} onHide={() => setRawEntry(null)} size="xl" centered scrollable>
            <Modal.Header closeButton style={{ background: '#161b22', borderBottom: '1px solid #30363d' }}>
                <Modal.Title style={{ fontSize: '0.9rem', fontFamily: 'monospace', color: '#e6edf3' }}>
                    Raw: {rawEntry ? entryKey(rawEntry) : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ background: '#0d1117', color: '#e6edf3' }}>
                {rawEntry && (
                    <JsonEditor
                        data={rawEntry.data as unknown as Record<string, unknown>}
                        viewOnly
                        collapse={2}
                        theme={githubDarkTheme}
                        rootName="data"
                    />
                )}
            </Modal.Body>
            <Modal.Footer style={{ background: '#161b22', borderTop: '1px solid #30363d' }}>
                <Button variant="secondary" size="sm" onClick={() => setRawEntry(null)}>Close</Button>
            </Modal.Footer>
        </Modal>
        </>
    );
}

export default MergeOpPage;
